import { useEffect, useMemo, useRef, useState } from "react";
import Tippy from "@tippyjs/react";
import { HiDocument, HiDocumentText, HiDownload, HiDuplicate, HiEmojiHappy, HiExclamationCircle, HiPencil, HiReply, HiTrash, HiPlay, HiPause } from "react-icons/hi";
import { animate, motion, useMotionValue, useTransform } from "motion/react";
import { Info, Pin, PinOff } from "lucide-react";
import ChatAvatar from "./ChatAvatar";
import ProfileCardTrigger from "./ProfileCardTrigger";
import MessageActionSheet from "./MessageActionSheet";
import { useIsTouch } from "./useMedia";
import { useChat } from "./ChatProvider";
import { fetchAttachmentUrl } from "../../controllers/chatController";
import {
  QUICK_REACTIONS,
  formatDuration,
  formatFileSize,
  formatTime,
  isAudio,
  isImage,
  isPdf,
  resolveFileUrl,
  mentionsToText,
  splitMessage,
  systemMessageText,
  userName,
} from "./chatUtils";

function MentionChip({ userId, mine }) {
  const { users, me } = useChat();
  if (userId === "everyone") {
    return (
      <span className={`inline rounded px-1 font-semibold ${mine ? "bg-white/20 text-white" : "bg-amber-200 text-amber-900 dark:bg-amber-500/30 dark:text-amber-100"}`}>
        @everyone
      </span>
    );
  }
  const isMe = userId === me?.id;
  const tone = mine
    ? "bg-white/20 text-white"
    : isMe
      ? "bg-amber-200 text-amber-900 dark:bg-amber-500/30 dark:text-amber-100"
      : "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200";
  return (
    <ProfileCardTrigger userId={userId} placement="top">
      <span className={`inline rounded px-1 font-semibold hover:underline ${tone}`}>@{userName(users[userId])}</span>
    </ProfileCardTrigger>
  );
}

function MessageText({ text, mine }) {
  return (
    <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
      {splitMessage(text).map((part, i) =>
        typeof part === "string" ? (
          part
        ) : part.mention ? (
          <MentionChip key={i} userId={part.mention} mine={mine} />
        ) : (
          <a
            key={i}
            href={part.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline break-all ${mine ? "text-white" : "text-blue-600 dark:text-blue-400"}`}
          >
            {part.url}
          </a>
        )
      )}
    </p>
  );
}

// Signed attachment links expire after 6 hours. Images fetch a fresh link when they fail to load;
// file links refresh on hover once they are older than 5 hours.
const STALE_AFTER_MS = 5 * 60 * 60 * 1000;

function useFreshUrl(attachment) {
  const [state, setState] = useState(() => ({
    url: attachment.url,
    downloadUrl: attachment.downloadUrl || attachment.url,
    fetchedAt: Date.now(),
    retried: false,
  }));
  const refresh = async ({ onlyIfStale = false } = {}) => {
    if (!attachment.id) return;
    if (onlyIfStale && Date.now() - state.fetchedAt < STALE_AFTER_MS) return;
    if (!onlyIfStale && state.retried) return;
    setState((s) => ({ ...s, retried: true }));
    try {
      const { url, downloadUrl } = await fetchAttachmentUrl(attachment.id);
      setState({ url, downloadUrl: downloadUrl || url, fetchedAt: Date.now(), retried: true });
    } catch {
      /* keep the current link */
    }
  };
  return [resolveFileUrl(state.url), refresh, resolveFileUrl(state.downloadUrl)];
}

function ImageAttachment({ attachment, onOpen }) {
  const [url, refresh] = useFreshUrl(attachment);
  if (!attachment.url) {
    return <div className="w-40 h-28 rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse" />;
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(attachment)}
      className="block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 cursor-zoom-in"
      aria-label={`View ${attachment.fileName}`}
      data-chat-attachment="image"
    >
      <img
        src={url}
        alt={attachment.fileName}
        onError={() => refresh()}
        className="max-w-[240px] max-h-60 rounded-lg object-cover border border-black/5"
        loading="lazy"
      />
    </button>
  );
}

function FileAttachment({ attachment, mine, onOpen }) {
  const [, refresh, downloadUrl] = useFreshUrl(attachment);
  const card = `flex items-center gap-3 rounded-lg max-w-[260px] ${
    mine ? "bg-white/15" : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
  }`;
  const hover = mine ? "hover:bg-white/25" : "hover:bg-gray-50 dark:hover:bg-gray-700";
  const details = (
    <span className="min-w-0 flex-1 text-left">
      <span className="block text-sm font-medium truncate">{attachment.fileName}</span>
      <span className="block text-xs opacity-75">
        {isPdf(attachment.mimeType) ? "PDF · " : ""}
        {formatFileSize(attachment.size)}
      </span>
    </span>
  );

  if (isPdf(attachment.mimeType) && attachment.id) {
    return (
      <div className={card} data-chat-attachment="pdf">
        <button type="button" onClick={() => onOpen(attachment)} className={`flex min-w-0 flex-1 items-center gap-3 rounded-l-lg px-3 py-2 ${hover}`} aria-label={`View ${attachment.fileName}`}>
          <HiDocumentText className="w-8 h-8 shrink-0 text-red-500" />
          {details}
        </button>
        <a
          href={downloadUrl || undefined}
          onMouseEnter={() => refresh({ onlyIfStale: true })}
          onFocus={() => refresh({ onlyIfStale: true })}
          className={`self-stretch flex items-center rounded-r-lg px-3 ${hover}`}
          aria-label={`Download ${attachment.fileName}`}
          title="Download"
        >
          <HiDownload className="w-4 h-4 opacity-75" />
        </a>
      </div>
    );
  }

  return (
    <a
      href={downloadUrl || undefined}
      onMouseEnter={() => refresh({ onlyIfStale: true })}
      onFocus={() => refresh({ onlyIfStale: true })}
      className={`${card} ${hover} px-3 py-2`}
      aria-label={`Download ${attachment.fileName}`}
      data-chat-attachment="file"
    >
      <HiDocument className="w-8 h-8 shrink-0 opacity-80" />
      {details}
      <HiDownload className="w-4 h-4 shrink-0 opacity-75" />
    </a>
  );
}

// Plays one voice note at a time across the whole app.
const PLAY_EVENT = "chat-voice-note-play";

function VoiceNotePlayer({ attachment, mine }) {
  const [url, refresh] = useFreshUrl(attachment);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0); // seconds
  const [mediaDuration, setMediaDuration] = useState(null);
  const duration = attachment.durationMs ? attachment.durationMs / 1000 : mediaDuration || 0;
  // Decorative waveform, stable per attachment.
  const bars = useMemo(() => {
    let seed = Number(attachment.id) || 7;
    return Array.from({ length: 32 }, () => {
      seed = (seed * 9301 + 49297) % 233280;
      return 0.25 + (seed / 233280) * 0.75;
    });
  }, [attachment.id]);

  useEffect(() => {
    const onOtherPlay = (e) => e.detail !== attachment.id && audioRef.current?.pause();
    window.addEventListener(PLAY_EVENT, onOtherPlay);
    return () => window.removeEventListener(PLAY_EVENT, onOtherPlay);
  }, [attachment.id]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: attachment.id }));
      audio.play().catch(() => refresh());
    } else {
      audio.pause();
    }
  };

  const seek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audio.currentTime = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * duration;
  };

  const progress = duration ? Math.min(1, position / duration) : 0;
  return (
    <div className={`flex items-center gap-2 rounded-xl px-2 py-1.5 w-[260px] max-w-full ${mine ? "bg-white/15" : "bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700"}`} data-chat-attachment="voice">
      {/* Voice notes are recorded speech with no transcript to caption. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src={url || undefined}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPosition(0);
        }}
        onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => Number.isFinite(e.currentTarget.duration) && setMediaDuration(e.currentTarget.duration)}
        onError={() => refresh()}
      />
      <button
        type="button"
        onClick={toggle}
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${mine ? "bg-white text-[#2470B7]" : "bg-[#2470B7] text-white"}`}
        aria-label={playing ? "Pause voice message" : "Play voice message"}
      >
        {playing ? <HiPause className="h-5 w-5" /> : <HiPlay className="h-5 w-5 translate-x-px" />}
      </button>
      <button type="button" onClick={seek} className="flex h-8 min-w-0 flex-1 items-center justify-between overflow-hidden" aria-label="Seek">
        {bars.map((height, i) => (
          <span
            key={i}
            className={`w-[3px] shrink-0 rounded-full ${i / bars.length < progress ? (mine ? "bg-white" : "bg-[#2470B7]") : mine ? "bg-white/40" : "bg-gray-300 dark:bg-gray-600"}`}
            style={{ height: `${Math.round(height * 100)}%` }}
          />
        ))}
      </button>
      <span className={`w-9 shrink-0 text-right text-[11px] tabular-nums ${mine ? "text-white/80" : "text-gray-500"}`}>
        {formatDuration((playing || position ? position : duration) * 1000)}
      </span>
    </div>
  );
}

function LinkPreviewCard({ preview, mine }) {
  const [imageFailed, setImageFailed] = useState(false);
  return (
    <a
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`mt-2 block overflow-hidden rounded-lg border max-w-[300px] ${
        mine ? "border-white/25 bg-white/10 hover:bg-white/15" : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
      }`}
      data-chat-link-preview
    >
      {preview.image && !imageFailed && (
        <img src={preview.image} alt="" referrerPolicy="no-referrer" loading="lazy" onError={() => setImageFailed(true)} className="h-36 w-full object-cover" />
      )}
      <span className="block px-3 py-2">
        <span className={`block text-[11px] uppercase tracking-wide ${mine ? "text-white/70" : "text-gray-500"}`}>{preview.siteName}</span>
        <span className="block text-sm font-semibold line-clamp-2">{preview.title}</span>
        {preview.description && <span className={`block text-xs line-clamp-2 ${mine ? "text-white/80" : "text-gray-600 dark:text-gray-300"}`}>{preview.description}</span>}
      </span>
    </a>
  );
}

function ReactionChips({ message, onToggle }) {
  const { users, me } = useChat();
  if (!message.reactions?.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {message.reactions.map(({ emoji, userIds }) => {
        const mineReacted = userIds.includes(me?.id);
        const who = userIds.map((id) => (id === me?.id ? "You" : userName(users[id]))).join(", ");
        return (
          <button
            type="button"
            key={emoji}
            title={who}
            onClick={() => onToggle(emoji)}
            className={`flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs border ${
              mineReacted
                ? "bg-blue-50 border-blue-300 dark:bg-blue-900/40 dark:border-blue-700"
                : "bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700"
            }`}
          >
            <span>{emoji}</span>
            <span className="text-gray-600 dark:text-gray-300">{userIds.length}</span>
          </button>
        );
      })}
    </div>
  );
}

function ReplyQuote({ replyTo, mine }) {
  const { users, me } = useChat();
  if (!replyTo) return null;
  const author = replyTo.senderId === me?.id ? "You" : userName(users[replyTo.senderId]);
  const text = replyTo.deleted
    ? "Message deleted"
    : mentionsToText(replyTo.body, users) || (replyTo.attachmentCount ? `📎 ${replyTo.attachmentCount} attachment(s)` : "");
  return (
    <div className={`mb-1 pl-2 border-l-2 text-xs ${mine ? "border-white/60 text-white/85" : "border-blue-400 text-gray-600 dark:text-gray-300"}`}>
      <span className="font-semibold block">{author}</span>
      <span className="line-clamp-2 break-words">{text}</span>
    </div>
  );
}

function ToolbarButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="p-1.5 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-100"
    >
      {children}
    </button>
  );
}

// Touch gestures: hold a message to open its action sheet; swipe it right to reply.
const LONG_PRESS_MS = 450;
const SWIPE_REPLY = 56; // px of (dampened) swipe that triggers a reply
const SWIPE_MAX = 80;
const SETTLE = { type: "tween", ease: "easeOut", duration: 0.2 };

function MessageItem({ message, showHeader, highlighted, receipt, onReply, onEdit, onDelete, onOpenAttachment, onInfo, compact }) {
  const { users, me, toggleReaction, retryMessage, discardMessage, setPinned } = useChat();
  const [pickerOpen, setPickerOpen] = useState(false);
  const touch = useIsTouch();
  const [sheetOpen, setSheetOpen] = useState(false);
  const swipeX = useMotionValue(0);
  const replyHint = useTransform(swipeX, [0, SWIPE_REPLY], [0, 1]);
  const gestureRef = useRef(null);
  const suppressClickRef = useRef(false);

  if (message.type === "system") {
    return (
      <div className="flex justify-center my-2 px-4">
        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-3 py-1 text-center">
          {systemMessageText(message, users, me?.id)}
        </span>
      </div>
    );
  }

  const mine = message.senderId === me?.id;
  const sender = users[message.senderId];
  const deleted = Boolean(message.deletedAt);
  const mentionsMe = !mine && (message.mentionedUserIds || []).includes(me?.id);
  const images = (message.attachments || []).filter((a) => isImage(a.mimeType));
  const voiceNotes = (message.attachments || []).filter((a) => isAudio(a.mimeType));
  const files = (message.attachments || []).filter((a) => !isImage(a.mimeType) && !isAudio(a.mimeType));
  const canAct = !deleted && !message.pending;

  const react = (emoji) => {
    setPickerOpen(false);
    setSheetOpen(false);
    toggleReaction(message.id, emoji).catch(() => {});
  };

  const endGesture = (e) => {
    const gesture = gestureRef.current;
    if (!gesture || e.pointerId !== gesture.id) return;
    clearTimeout(gesture.timer);
    gestureRef.current = null;
    if (gesture.mode === "swipe") {
      if (swipeX.get() >= SWIPE_REPLY) onReply(message);
      animate(swipeX, 0, SETTLE);
    }
  };

  const touchHandlers =
    touch && canAct
      ? {
          onPointerDown: (e) => {
            if (e.pointerType !== "touch") return;
            suppressClickRef.current = false;
            const gesture = { id: e.pointerId, x: e.clientX, y: e.clientY, mode: null };
            gesture.timer = setTimeout(() => {
              gesture.mode = "press";
              suppressClickRef.current = true; // the tap that ends a long press shouldn't open images/links
              navigator.vibrate?.(10);
              setSheetOpen(true);
            }, LONG_PRESS_MS);
            gestureRef.current = gesture;
          },
          onPointerMove: (e) => {
            const gesture = gestureRef.current;
            if (!gesture || e.pointerId !== gesture.id || gesture.mode === "press") return;
            const dx = e.clientX - gesture.x;
            const dy = e.clientY - gesture.y;
            if (!gesture.mode && Math.hypot(dx, dy) > 10) {
              clearTimeout(gesture.timer);
              gesture.mode = dx > 0 && Math.abs(dx) > Math.abs(dy) ? "swipe" : "scroll";
            }
            if (gesture.mode !== "swipe") return;
            const offset = Math.min(Math.max(dx, 0) * 0.7, SWIPE_MAX);
            if (!gesture.buzzed && offset >= SWIPE_REPLY) {
              gesture.buzzed = true;
              navigator.vibrate?.(8);
            }
            swipeX.set(offset);
          },
          onPointerUp: endGesture,
          onPointerCancel: endGesture,
          onContextMenu: (e) => e.preventDefault(),
          onClickCapture: (e) => {
            if (suppressClickRef.current) {
              suppressClickRef.current = false;
              e.preventDefault();
              e.stopPropagation();
            }
          },
        }
      : {};

  const closeSheetAnd = (fn) => () => {
    setSheetOpen(false);
    fn();
  };
  const sheetActions = [
    { label: "Reply", icon: <HiReply className="w-5 h-5" />, onClick: closeSheetAnd(() => onReply(message)) },
    message.body && {
      label: "Copy text",
      icon: <HiDuplicate className="w-5 h-5" />,
      onClick: closeSheetAnd(() => navigator.clipboard?.writeText(mentionsToText(message.body, users)).catch(() => {})),
    },
    message.type === "text" && {
      label: message.pinnedAt ? "Unpin" : "Pin",
      icon: message.pinnedAt ? <PinOff className="w-5 h-5" /> : <Pin className="w-5 h-5" />,
      onClick: closeSheetAnd(() => setPinned(message.id, !message.pinnedAt).catch(() => {})),
    },
    mine && onInfo && { label: "Message info", icon: <Info className="w-5 h-5" />, onClick: closeSheetAnd(() => onInfo(message)) },
    mine && { label: "Edit", icon: <HiPencil className="w-5 h-5" />, onClick: closeSheetAnd(() => onEdit(message)) },
    mine && { label: "Delete", icon: <HiTrash className="w-5 h-5" />, danger: true, onClick: closeSheetAnd(() => onDelete(message)) },
  ].filter(Boolean);
  const sheetPreview = message.body
    ? mentionsToText(message.body, users)
    : (message.attachments || []).map((a) => a.fileName).join(", ");

  return (
    <div id={message.id ? `chat-msg-${message.id}` : undefined} className={`relative ${showHeader ? "mt-3" : "mt-0.5"}`}>
      {touch && canAct && (
        <motion.span
          style={{ opacity: replyHint, scale: replyHint }}
          className="pointer-events-none absolute left-3 top-1/2 -mt-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500"
          aria-hidden="true"
        >
          <HiReply className="w-4 h-4" />
        </motion.span>
      )}
      <motion.div
        {...touchHandlers}
        style={touch && canAct ? { x: swipeX, touchAction: "pan-y", WebkitTouchCallout: "none" } : undefined}
        className={`group flex gap-2 px-3 ${compact ? "relative" : ""} ${touch ? "select-none" : ""} ${mine ? "flex-row-reverse" : ""}`}
        data-chat-message={message.id || undefined}
      >
        {!mine && (
          <div className="w-8 shrink-0">
            {showHeader && (
              <ProfileCardTrigger userId={message.senderId}>
                <ChatAvatar name={userName(sender)} src={sender?.avatarThumbUrl} seed={message.senderId} size="sm" />
              </ProfileCardTrigger>
            )}
          </div>
        )}

        <div className={`min-w-0 flex flex-col ${mine ? "items-end" : "items-start"} ${compact ? "max-w-[82%]" : "max-w-[70%]"}`}>
          {showHeader && (
            <div className={`flex items-baseline gap-2 mb-0.5 text-xs ${mine ? "flex-row-reverse" : ""}`}>
              {!mine && (
                <ProfileCardTrigger userId={message.senderId}>
                  <span className="font-semibold text-gray-700 dark:text-gray-200 hover:underline">{userName(sender)}</span>
                </ProfileCardTrigger>
              )}
              <span className="text-gray-400">{formatTime(message.createdAt)}</span>
            </div>
          )}
          {message.pinnedAt && !deleted && (
            <span className="mb-0.5 inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 dark:text-amber-400">
              <Pin className="h-3 w-3" /> Pinned
            </span>
          )}

          {/* In the narrow dock window the toolbar floats in the far top corner of the message row instead of sitting
              beside the bubble, so wide content (images, PDF cards) can't push it outside the window. */}
          <div className={`flex items-center ${compact ? "" : "relative gap-1"} ${mine ? "flex-row-reverse" : ""}`}>
            <div
              className={`rounded-2xl px-3 py-2 transition-shadow ${
                deleted
                  ? "bg-transparent border border-dashed border-gray-300 dark:border-gray-600 text-gray-400 italic"
                  : mine
                    ? "bg-[#2470B7] text-white"
                    : mentionsMe
                      ? "bg-amber-50 dark:bg-amber-900/30 text-gray-800 dark:text-gray-100 ring-1 ring-amber-300 dark:ring-amber-600"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100"
              } ${message.pending && !message.failed ? "opacity-70" : ""} ${
                highlighted ? "ring-4 ring-yellow-300 dark:ring-yellow-600" : ""
              }`}
            >
              {deleted ? (
                <p className="text-sm">This message was deleted</p>
              ) : (
                <>
                  <ReplyQuote replyTo={message.replyTo} mine={mine} />
                  {images.length > 0 && (
                    <div className={`flex flex-wrap gap-1 ${message.body || files.length ? "mb-2" : ""}`}>
                      {images.map((a) => (
                        <ImageAttachment key={a.id || a.fileName} attachment={a} onOpen={onOpenAttachment} />
                      ))}
                    </div>
                  )}
                  {voiceNotes.length > 0 && (
                    <div className={`flex flex-col gap-1 ${message.body || files.length ? "mb-2" : ""}`}>
                      {voiceNotes.map((a) => (
                        <VoiceNotePlayer key={a.id || a.fileName} attachment={a} mine={mine} />
                      ))}
                    </div>
                  )}
                  {files.length > 0 && (
                    <div className={`flex flex-col gap-1 ${message.body ? "mb-2" : ""}`}>
                      {files.map((a) => (
                        <FileAttachment key={a.id || a.fileName} attachment={a} mine={mine} onOpen={onOpenAttachment} />
                      ))}
                    </div>
                  )}
                  {message.body && <MessageText text={message.body} mine={mine} />}
                  {message.linkPreview && <LinkPreviewCard preview={message.linkPreview} mine={mine} />}
                  {message.editedAt && (
                    <span className={`block text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-gray-400"}`}>edited</span>
                  )}
                </>
              )}
            </div>

            {canAct && !touch && (
              <div
                data-chat-toolbar
                className={`flex items-center bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm transition-opacity ${
                  compact ? `absolute top-0 z-10 ${mine ? "left-3" : "right-3"}` : ""
                } ${
                  pickerOpen
                    ? "opacity-100"
                    : `opacity-0 group-hover:opacity-100 focus-within:opacity-100 ${
                        compact ? "pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto" : ""
                      }`
                }`}
              >
                <Tippy
                  theme="irg-plain"
                  arrow={false}
                  visible={pickerOpen}
                  onClickOutside={() => setPickerOpen(false)}
                  interactive
                  placement="top"
                  appendTo={() => document.body}
                  zIndex={90}
                  content={
                    <div className="flex gap-1 p-1 bg-white dark:bg-gray-800 rounded-full shadow-lg border border-gray-200 dark:border-gray-700">
                      {QUICK_REACTIONS.map((emoji) => (
                        <button key={emoji} type="button" onClick={() => react(emoji)} className="text-xl w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-110 transition-transform">
                          {emoji}
                        </button>
                      ))}
                    </div>
                  }
                >
                  <span>
                    <ToolbarButton label="Add reaction" onClick={() => setPickerOpen((v) => !v)}>
                      <HiEmojiHappy className="w-4 h-4" />
                    </ToolbarButton>
                  </span>
                </Tippy>
                <ToolbarButton label="Reply" onClick={() => onReply(message)}>
                  <HiReply className="w-4 h-4" />
                </ToolbarButton>
                {message.type === "text" && (
                  <ToolbarButton label={message.pinnedAt ? "Unpin" : "Pin"} onClick={() => setPinned(message.id, !message.pinnedAt).catch(() => {})}>
                    {message.pinnedAt ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                  </ToolbarButton>
                )}
                {mine && onInfo && (
                  <ToolbarButton label="Message info" onClick={() => onInfo(message)}>
                    <Info className="w-4 h-4" />
                  </ToolbarButton>
                )}
                {mine && (
                  <>
                    <ToolbarButton label="Edit" onClick={() => onEdit(message)}>
                      <HiPencil className="w-4 h-4" />
                    </ToolbarButton>
                    <ToolbarButton label="Delete" onClick={() => onDelete(message)}>
                      <HiTrash className="w-4 h-4" />
                    </ToolbarButton>
                  </>
                )}
              </div>
            )}
          </div>

          {!deleted && <ReactionChips message={message} onToggle={react} />}

          {message.failed && (
            <div className="flex items-center gap-2 mt-1 text-xs text-red-600">
              <HiExclamationCircle className="w-4 h-4" />
              <span>{message.error}</span>
              <button type="button" className="underline" onClick={() => retryMessage(message.conversationId, message.clientId)}>
                Retry
              </button>
              <button type="button" className="underline" onClick={() => discardMessage(message.conversationId, message.clientId)}>
                Discard
              </button>
            </div>
          )}
          {message.pending && !message.failed && message.queued && (
            <span className="flex items-center gap-2 text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
              Waiting for network – sends when you&apos;re back online
              <button type="button" className="underline" onClick={() => discardMessage(message.conversationId, message.clientId)}>
                Cancel
              </button>
            </span>
          )}
          {message.pending && !message.failed && !message.queued && <span className="text-[10px] text-gray-400 mt-0.5">Sending…</span>}
          {receipt && (
            <button
              type="button"
              onClick={() => onInfo && onInfo(message)}
              className="mt-0.5 flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title={receipt.title}
              data-chat-receipt
            >
              {receipt.readers?.length > 0 && (
                <span className="flex -space-x-1.5">
                  {receipt.readers.slice(0, 5).map((id) => (
                    <span key={id} className="rounded-full ring-2 ring-white dark:ring-gray-900">
                      <ChatAvatar name={userName(users[id])} src={users[id]?.avatarThumbUrl} seed={id} size="xs" />
                    </span>
                  ))}
                </span>
              )}
              {receipt.label}
            </button>
          )}
        </div>
      </motion.div>
      {sheetOpen && (
        <MessageActionSheet
          preview={sheetPreview}
          reactions={QUICK_REACTIONS}
          onReact={react}
          actions={sheetActions}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  );
}

export default MessageItem;
